<script>
import Modal from 'src/components/Modal'

export default {
  name: 'ModalAlert',
  components: { Modal },
  props: {
    // Kích thước modal: md (mặc định) | sm | lg | popup
    size: {
      type: String,
      default: 'md'
    },
    // success | error | warning | info | question | '' (không hiện icon)
    type: {
      type: String,
      default: ''
    },
    // Tiêu đề hiển thị ở header. Để trống thì ẩn header.
    title: {
      type: String,
      default: ''
    },
    // Nội dung body, cho phép HTML (giống `html` của sweetalert)
    html: {
      type: String,
      default: ''
    },
    showCancelButton: {
      type: Boolean,
      default: false
    },
    TextCancelButton: {
      type: String,
      default: ''
    },
    showConfirmButton: {
      type: Boolean,
      default: true
    },
    TextConfirmButton: {
      type: String,
      default: ''
    },
    showCustomButton: {
      type: Boolean,
      default: false
    },
    TextCustomButton: {
      type: String,
      default: ''
    },
    // Căn nội dung: 'center' (mặc định) | 'left' | 'right'
    align: {
      type: String,
      default: 'center'
    },
    // Số ms trước khi modal tự động đóng. 0 (mặc định) = không tự đóng
    autoClose: {
      type: Number,
      default: 0
    }
  },
  computed: {
    iconClass() {
      switch (this.type) {
        case 'success':
          return 'icofont icofont-check-circled'
        case 'error':
          return 'icofont icofont-close-circled'
        case 'warning':
          return 'icofont icofont-warning-alt'
        case 'info':
          return 'icofont icofont-info-circle'
        case 'question':
          return 'icofont icofont-question-circle'
        default:
          return ''
      }
    },
    hasFooter() {
      return this.showConfirmButton || this.showCancelButton || this.showCustomButton
    },
    // Mặc định căn giữa, có thể override qua prop `align`
    contentAlign() {
      if (this.align === 'left') return 'text-left'
      if (this.align === 'right') return 'text-right'
      return 'text-center'
    }
  },
  methods: {
    onConfirm() {
      this.$emit('result', { action: 'confirm' })
    },
    onCancel() {
      this.$emit('result', { action: 'cancel' })
    },
    onCustom() {
      this.$emit('result', { action: 'custom' })
    },
    onClose() {
      this.$emit('result', { action: 'close' })
    }
  }
}
</script>

<template>
  <Modal
    :size="size"
    :dismissible="true"
    :auto-close="autoClose"
    :class="{ 'modal-alert-no-title': !title }"
    @close="onClose"
  >
    <template #header>
      <p v-if="title">
        {{ title }}
      </p>
      <span v-else />
    </template>
    <template #body>
      <div class="modal-alert-body">
        <i
          v-if="iconClass"
          :class="['modal-alert-icon', `modal-alert-icon--${type}`, iconClass]"
        />
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div
          :class="['modal-alert-content', contentAlign]"
          v-html="html"
        />
      </div>
    </template>
    <template #footer>
      <div
        v-if="hasFooter"
        class="modal-alert-footer"
      >
        <!-- Secondary (Cancel) đứng bên trái trong nhóm, nền trắng -->
        <button
          v-if="showCancelButton"
          class="btn modal-alert-btn modal-alert-btn--secondary"
          @click="onCancel"
        >
          {{ TextCancelButton || $t('button.close') }}
        </button>
        <button
          v-if="showCustomButton"
          class="btn modal-alert-btn modal-alert-btn--secondary"
          @click="onCustom"
        >
          {{ TextCustomButton }}
        </button>
        <!-- Primary (Confirm) đứng bên phải, nền #BE185A -->
        <button
          v-if="showConfirmButton"
          class="btn modal-alert-btn modal-alert-btn--primary"
          @click="onConfirm"
        >
          {{ TextConfirmButton || $t('button.ok') }}
        </button>
      </div>
      <p v-else />
    </template>
  </Modal>
</template>

<style lang="sass" scoped>
$primary-color: #BE185A
.modal-alert-body
    padding: 0.5em 0
    text-align: center
// Khi không có title: bỏ padding của header rỗng để icon không bị đẩy xuống
.modal-alert-no-title ::v-deep .modal-header
    padding: 0
.modal-alert-icon
    // Glyph 80px (line-height 80px) + 4px padding mỗi bên -> tổng 88px, căn giữa
    display: inline-block
    width: 80px
    height: 80px
    padding: 4px
    box-sizing: content-box
    font-size: 80px
    line-height: 80px
    text-align: center
    margin: 0 0 0.4em
    &.modal-alert-icon--success
        color: #28a745
    &.modal-alert-icon--error
        color: #dc3545
    &.modal-alert-icon--warning
        color: #ffc107
    &.modal-alert-icon--info
        color: #17a2b8
    &.modal-alert-icon--question
        color: #6c757d
.modal-alert-content
    font-family: NotoSanJP
    font-size: 16px
    color: rgba(43, 43, 43, 0.54)
    word-break: break-word
    &.text-center
        text-align: center
    &.text-left
        text-align: left
    &.text-right
        text-align: right
.modal-alert-footer
    display: flex
    align-items: center
    // Các nút căn giữa, thứ tự Secondary -> Custom -> Primary
    justify-content: center
    width: 100%
.modal-alert-btn
    height: 30px
    padding: 0 1em
    display: inline-flex
    align-items: center
    justify-content: center
    font-family: NotoSanJP
    font-size: 14px
    line-height: 1
    border-radius: 0.25em
    margin-left: 0.5em
    &:first-child
        margin-left: 0
.modal-alert-btn--primary
    background-color: $primary-color
    border: 1px solid $primary-color
    color: #fff
    &:hover
        background-color: darken($primary-color, 6%)
        border-color: darken($primary-color, 6%)
.modal-alert-btn--secondary
    background-color: #fff
    border: 1px solid #d0d0d0
    color: #333
    &:hover
        background-color: #f5f5f5
</style>
